import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, tokens, type Account } from './api';

/**
 * Who is signed in to the portal: one of a client's sign-ins — the company's
 * own, set on its form in the admin, or one of its contacts', set on theirs
 * (2026-10-11). Everyone signed in for a client sees and does the same.
 */

interface AuthContextValue {
  account: Account | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // Whatever one sign-in fetched must never show in the next one on the same
  // browser — another client's tickets, or a colleague's page left cached. As in
  // the admin: a page fetched under 20 s ago would be shown as it was.
  const forgetSession = useCallback(() => queryClient.clear(), [queryClient]);

  const load = useCallback(async () => {
    if (!tokens.access) {
      setAccount(null);
      setLoading(false);
      return;
    }
    try {
      setAccount(await api.get<Account>('/auth/me'));
    } catch {
      tokens.clear();
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // The API client fires this when a refresh fails — a session that ended
    // elsewhere (a new password, the account switched off) clears the page at once.
    const onSignedOut = () => {
      forgetSession();
      setAccount(null);
    };
    window.addEventListener('viganium:portal-signed-out', onSignedOut);
    return () => window.removeEventListener('viganium:portal-signed-out', onSignedOut);
  }, [load, forgetSession]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const result = await api.post<{ accessToken: string; refreshToken: string; account: Account }>('/auth/login', {
        username,
        password,
      });
      tokens.set(result.accessToken, result.refreshToken);
      // The session before may have ended without a sign-out — an expired token.
      forgetSession();
      setAccount(result.account);
    },
    [forgetSession],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout', { refreshToken: tokens.refresh ?? undefined });
    } catch {
      // Signing out here matters more than the server hearing about it.
    }
    tokens.clear();
    forgetSession();
    setAccount(null);
  }, [forgetSession]);

  const value = useMemo(() => ({ account, loading, signIn, signOut }), [account, loading, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
